// Tiny external store for server-driven state (sessions, ports, connection, desktop, toasts).

import { useSyncExternalStore } from 'react';
import type { ClosedSession, LoginFields, MachineLoad, Port, Session } from './net/protocol';
import type { NetInfo } from './net/quality';
import type { TransportKind } from './net/transport';

export type ConnState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'unauthorized';

/** The signed-in user (normalized from GET /api/me, see api.normalizeMe). */
export interface Me {
  id: string;
  email: string;
  role: 'admin' | 'member';
  disabled: boolean;
  created: string | null;
  lastLogin: string | null;
  machine: {
    id: string;
    status: string;
    owner: string | null;
    vcpu?: number;
    memoryGB?: number;
    diskGB?: number;
    region?: string;
    tier?: string;
  } | null;
  baseDomain?: string;
  previewSuffix?: string;
  scheme?: string;
  /** Journey steps reached (hub journey.go): signup, machine, signin, setup, onboarded. */
  journey: string[];
  /** Apps offered for download: the Mac, Windows and Android apps, each once it's published. */
  apps: { mac: boolean; windows: boolean; android: boolean };
  /** Setting up (hub gate.go): done = onboarded; required = the web waits on /start until done. */
  /** offer: Magic Setup is offered until a setup runs (done) or the person says don't ask again (declined). */
  setup: { done: boolean; required: boolean; offer: 'ask' | 'done' | 'declined' };
  settings: Settings;
  /** Plans (hub billing.go): enabled = paid for here; plan = this account's, or null. */
  billing: { enabled: boolean; plan: import('./api').Plan | null };
}

/** "Open links from your cloud machine" (agent openurl.go): auto (the default), on this computer, or in the cloud machine's browser. */
export type LinksSetting = 'auto' | 'device' | 'machine';

/** How every list of sessions is organized (hub settings.go, proto organize.go). */
export type Organize = 'none' | 'folder' | 'program';

/** What a person sets once for all their devices (hub settings.go). */
export interface Settings {
  links: LinksSetting;
  organize: Organize;
  /** The CLI's session lists show the selected session's screen ('on' | 'off'); kept as is here. */
  sessionPreview: 'on' | 'off';
  /** Not saved by the hub yet: each shows up in Settings once the hub sends it. */
  phone?: boolean;
  sound?: boolean;
  fontSize?: number;
  cursor?: 'block' | 'bar' | 'underline';
}

/** Sign-in state as last seen from GET /api/me. */
export type Auth =
  | { kind: 'unknown' }
  | { kind: 'in' }
  | { kind: 'out'; github: boolean }
  | { kind: 'error'; message: string };

export interface Toast {
  id: number;
  title: string;
  body?: string;
  /** Next to the title, smaller: where a session's toast is from (its folder). */
  meta?: string;
  session?: number;
  /** A newer toast with the same key replaces the older one (e.g. 'net'). */
  key?: string;
  kind?: 'info' | 'error';
  /** A button on the notice (e.g. Copy, when the browser needs a click to take the clipboard). */
  action?: { label: string; run: () => void };
}

/** A link a program on the machine asked to open (see OpenLinks). */
export interface OpenLink {
  id: string;
  session: number;
  url: string;
  target: 'laptop' | 'preview' | 'machine';
  /** What finishing it needs: nothing of the machine's, a server on it, or a callback to it. */
  reason: 'link' | 'server' | 'signin';
  app: string;
  port?: number;
  path?: string;
  /** signin: its callback server closed, i.e. the sign-in went through. */
  done?: boolean;
  at: number;
}

/** A note about files dropped into a session (see term/drops.ts and Transfers). */
export interface Transfer {
  id: number;
  session: number;
  kind: 'notice';
  text: string;
  /** When it was shown (the pane's toast stack puts the newest on top). */
  at: number;
}

/** Files on their way into a session, or why they failed (see term/drops.ts and UploadBanner). */
export interface Upload {
  id: number;
  session: number;
  /** The file's name, or "N files". */
  name: string;
  /** Bytes sent so far, and in all. */
  sent: number;
  total: number;
  /** Set when it failed: the reason. */
  error?: string;
}

export interface AppState {
  me: Me | null;
  auth: Auth;
  conn: ConnState;
  connError: string | null;
  transport: TransportKind | null;
  rtt: number | null;
  /** The network's state and numbers from the hub's reports (net/quality.ts); null until the first. */
  net: NetInfo | null;
  sessions: Session[];
  sessionsLoaded: boolean;
  /** Sessions that closed on its own, the latest first (Recently closed). */
  closed: ClosedSession[];
  ports: Port[];
  desktopRunning: boolean;
  desktopBusy: boolean;
  desktopError: string | null;
  /** Browser tab id controlling the desktop stream ('' = nobody); see DesktopPane. */
  desktopController: string;
  /** This tab controls the desktop stream (drives the touch key bar on the Desktop pane). */
  desktopControlling: boolean;
  /** The desktop stream on a bad network: its mode, frame rate, picture size and the user's policy (StreamMode). */
  desktopNet: { mode: 'normal' | 'low'; policy: 'auto' | 'full' | 'low'; fps: number; scale: number } | null;
  /** The machine's load (busy, full) as its agent last said, and the level its banner was hidden at. */
  machineLoad: MachineLoad;
  loadHiddenAt: MachineLoad['state'] | null;
  /** An agent driving the desktop (the "claude is using the desktop" pill). Nothing in the hub or agent protocol reports one yet, so this stays null. */
  desktopDriver: { name: string; task: string } | null;
  toasts: Toast[];
  /** Links programs on the machine asked to open, newest last (OpenLinks). */
  openLinks: OpenLink[];
  /** The login form focused in the machine's browser (LoginOverlay); null: none. */
  loginFields: LoginFields | null;
  /** Where the desktop's picture is in the stream frame, in CSS pixels (from its bridge). */
  desktopView: { x: number; y: number; w: number; h: number } | null;
  /** Notes about dropped or pasted files, per session (Transfers). */
  transfers: Transfer[];
  /** Uploads of dropped or pasted files, per session (UploadBanner). */
  uploads: Upload[];
  /** The session that currently has keyboard focus (null: none / desktop). */
  focusedSession: number | null;
}

type Listener = () => void;

let state: AppState = {
  me: null,
  auth: { kind: 'unknown' },
  conn: 'idle',
  connError: null,
  transport: null,
  rtt: null,
  net: null,
  sessions: [],
  sessionsLoaded: false,
  closed: [],
  ports: [],
  desktopRunning: false,
  desktopBusy: false,
  desktopNet: null,
  machineLoad: { state: 'ok' },
  loadHiddenAt: null,
  desktopDriver: null,
  desktopError: null,
  desktopController: '',
  desktopControlling: false,
  toasts: [],
  openLinks: [],
  loginFields: null,
  desktopView: null,
  transfers: [],
  uploads: [],
  focusedSession: null,
};
const listeners = new Set<Listener>();

export const store = {
  get: () => state,
  set(patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)) {
    const p = typeof patch === 'function' ? patch(state) : patch;
    let changed = false;
    for (const k in p) {
      if ((state as unknown as Record<string, unknown>)[k] !== (p as Record<string, unknown>)[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    state = { ...state, ...p };
    listeners.forEach((l) => l());
  },
  subscribe(l: Listener) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useStore<T>(sel: (s: AppState) => T): T {
  return useSyncExternalStore(store.subscribe, () => sel(state), () => sel(state));
}

let toastId = 1;
export function toast(t: Omit<Toast, 'id'>, ttlMs = 6000) {
  const id = toastId++;
  // One toast per session: a newer notification replaces the older one.
  store.set((s) => ({
    toasts: [
      ...s.toasts.filter((x) => (t.session === undefined || x.session !== t.session) && (t.key === undefined || x.key !== t.key)),
      { ...t, id },
    ].slice(-5),
  }));
  if (ttlMs > 0) armToast(id, ttlMs);
  return id;
}

/* Each toast's time left. While the person reads the stack (hovered or focused) the clocks stop, and
   start again from where they were, with at least 1.5 s so nothing goes the moment the pointer leaves. */
const toastClocks = new Map<number, { left: number; since: number; timer?: ReturnType<typeof setTimeout> }>();
let toastsHeld = false;
function armToast(id: number, ms: number) {
  toastClocks.set(id, { left: ms, since: Date.now(), timer: toastsHeld ? undefined : setTimeout(() => dismissToast(id), ms) });
}
export function holdToasts() {
  if (toastsHeld) return;
  toastsHeld = true;
  const now = Date.now();
  for (const c of toastClocks.values()) {
    if (c.timer === undefined) continue;
    clearTimeout(c.timer);
    c.timer = undefined;
    c.left -= now - c.since;
  }
}
export function releaseToasts() {
  if (!toastsHeld) return;
  toastsHeld = false;
  const now = Date.now();
  for (const [id, c] of toastClocks) {
    c.since = now;
    c.left = Math.max(c.left, 1500);
    c.timer = setTimeout(() => dismissToast(id), c.left);
  }
}

/** A failure's message, for a toast's body. */
export const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function dismissToast(id: number) {
  const c = toastClocks.get(id);
  if (c?.timer !== undefined) clearTimeout(c.timer);
  toastClocks.delete(id);
  store.set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
}

/** The machine's name for notices ("dev1"): places are named, never "your machine". */
export function machineName(): string {
  return store.get().me?.machine?.id || 'the cloud machine';
}

const RANK = { ok: 0, busy: 1, full: 2 } as const;

/** The load's banner shows: busy or full, and not hidden at this level or a worse one (spec "Machine load"). */
export const showsLoad = (s: Pick<AppState, 'machineLoad' | 'loadHiddenAt'>) =>
  RANK[s.machineLoad.state] > RANK[s.loadHiddenAt ?? 'ok'];
